import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import Mariposa from "@/components/Mariposa";
import TierBadge from "@/components/TierBadge";
import { TIER_META, normalizeTier } from "@/constants";
import { Search, Filter, ExternalLink, Sparkles } from "lucide-react";

const PUBLIC_API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TIER_FILTERS = [
  { key: "", label: "Todos los niveles" },
  { key: "partner", label: TIER_META.partner.label },
  { key: "key", label: TIER_META.key.label },
  { key: "challenge", label: TIER_META.challenge.label },
];

export default function PublicDirectorio() {
  const [data, setData] = useState(null);
  const [tier, setTier] = useState("");
  const [category, setCategory] = useState("");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    const params = {};
    if (tier) params.tier = tier;
    if (category) params.category = category;
    if (q.trim()) params.q = q.trim();
    axios.get(`${PUBLIC_API}/public/directorio`, { params })
      .then((r) => setData(r.data))
      .catch(() => setData({ items: [], total: 0, tier_counts: {}, categories: [] }))
      .finally(() => setLoading(false));
  }, [tier, category, q]);

  const categories = data?.categories || [];
  const totalCelebrated = useMemo(
    () => Object.values(data?.tier_counts || {}).reduce((a, b) => a + b, 0),
    [data]
  );

  return (
    <div className="min-h-screen paper" data-testid="public-directorio-page">
      {/* Hero */}
      <section className="max-w-6xl mx-auto px-6 pt-12 pb-6">
        <div className="font-hand text-xl text-[var(--lala-blue)]">la familia LALA Elite</div>
        <h1 className="font-display text-5xl md:text-6xl mt-1 text-[var(--lala-blue)]">
          Proveedores que crecen con Lala
        </h1>
        <p className="text-[var(--ink-soft)] mt-3 max-w-2xl text-lg leading-relaxed">
          Estos proveedores forman parte de la red de colaboración y confianza compartida de Grupo Lala.
          Juntos cuidamos calidad, costo, continuidad, innovación, sustentabilidad y la relación día a día —
          y juntos llegamos a millones de familias. Son <em>la estrella que brilla</em>.
        </p>
        <div className="mt-4 inline-flex items-center gap-2 text-sm text-[var(--lala-blue)]" data-testid="dir-stats">
          <Sparkles size={14} />
          <span><strong>{totalCelebrated}</strong> proveedores celebrados ·{" "}
            <strong>{data?.tier_counts?.partner || 0}</strong> Partner ·{" "}
            <strong>{data?.tier_counts?.key || 0}</strong> Key ·{" "}
            <strong>{data?.tier_counts?.challenge || 0}</strong> Challenger
          </span>
        </div>
      </section>

      {/* Filters */}
      <section className="max-w-6xl mx-auto px-6 mb-6 sketch-border bg-[var(--cream-soft)] p-4"
        data-testid="dir-filters">
        <div className="grid md:grid-cols-3 gap-3">
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--ink-soft)]" />
            <input value={q} onChange={(e) => setQ(e.target.value)}
              placeholder="Buscar por nombre…"
              data-testid="dir-search"
              className="input-field !pl-9 w-full" />
          </div>
          <div className="flex items-center gap-2">
            <Filter size={14} className="text-[var(--ink-soft)]" />
            <select value={tier} onChange={(e) => setTier(e.target.value)}
              data-testid="dir-tier-filter" className="input-field flex-1">
              {TIER_FILTERS.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
            </select>
          </div>
          <select value={category} onChange={(e) => setCategory(e.target.value)}
            data-testid="dir-category-filter" className="input-field">
            <option value="">Todas las categorías</option>
            {categories.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
      </section>

      {/* Grid */}
      <section className="max-w-6xl mx-auto px-6 pb-16">
        {loading && <div className="text-center py-12 font-hand text-2xl text-[var(--lala-blue)]">Cargando…</div>}
        {!loading && (data?.items || []).length === 0 && (
          <div className="text-center py-16" data-testid="dir-empty">
            <div className="font-hand text-2xl text-[var(--lala-blue)]">Sin resultados.</div>
            <div className="text-sm text-[var(--ink-soft)] mt-2">Prueba ajustando los filtros.</div>
          </div>
        )}
        {!loading && (data?.items || []).length > 0 && (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="dir-grid">
            {data.items.map((s) => (
              <DirectorioCard key={s.id} supplier={s} />
            ))}
          </div>
        )}
      </section>

      {/* CTA footer */}
      <section className="bg-[var(--lala-blue)] text-[var(--cream)] py-12">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <Mariposa size={70} animated palette="lala" className="mx-auto" />
          <div className="font-hand text-2xl mt-2">¿Eres proveedor de Lala?</div>
          <h2 className="font-display text-3xl mt-1">Construyamos juntos tu estándar Elite.</h2>
          <p className="opacity-90 mt-3">
            Suma tu estrella a la red. Ganas visibilidad, contenido exclusivo y crecimiento junto con la familia Lala.
          </p>
          <Link to="/" className="sketch-pill bg-[var(--cream)] text-[var(--lala-blue)] px-5 py-2 text-sm font-semibold inline-flex items-center gap-2 mt-5">
            Conoce el programa <ExternalLink size={14} />
          </Link>
        </div>
      </section>

      <footer className="bg-[var(--cream-soft)] border-t border-[var(--line)] py-6 text-center text-xs text-[var(--ink-soft)]">
        Lala Elite · Un programa de Grupo Lala · MMXXVI
      </footer>
    </div>
  );
}

function DirectorioCard({ supplier }) {
  const meta = TIER_META[normalizeTier(supplier.tier)] || TIER_META.commercial;
  return (
    <Link
      to={`/sello/${supplier.id}`}
      data-testid={`dir-card-${supplier.id}`}
      className="sketch-border bg-[var(--cream-soft)] p-5 hover:shadow-lg transition-all hover:-translate-y-0.5 block group relative"
      style={{ borderColor: meta.color }}
    >
      {supplier.is_best_of_category && (
        <div
          data-testid={`dir-best-badge-${supplier.id}`}
          className="absolute -top-2 -right-2 px-2.5 py-1 rounded-full text-[9px] font-bold uppercase tracking-widest text-white flex items-center gap-1 shadow-md"
          style={{ background: "linear-gradient(135deg, #FFC400 0%, #E0A800 100%)", color: "#11142B" }}
          title={supplier.best_of_category_label || "Mejor de su categoría"}
        >
          <Sparkles size={10} /> {supplier.best_of_category_label || "Mejor de su categoría"}
        </div>
      )}
      <div className="flex items-start justify-between gap-3 mb-3">
        <Mariposa size={48} animated palette="lala" />
        <TierBadge tier={supplier.tier} size="sm" />
      </div>
      <h3 className="font-display text-2xl text-[var(--lala-blue)] leading-tight group-hover:text-[var(--lala-blue-deep)] transition-colors">
        {supplier.name}
      </h3>
      <div className="text-xs text-[var(--ink-soft)] mt-1">{supplier.category}</div>
      <div className="font-hand text-lg mt-3" style={{ color: meta.color }}>{meta.tagline}</div>
      {supplier.description && (
        <p className="text-sm text-[var(--ink-soft)] mt-2 line-clamp-2">{supplier.description}</p>
      )}
      <div className="text-[10px] text-[var(--ink-soft)] mt-4 inline-flex items-center gap-1">
        <ExternalLink size={10} /> Ver sello · {supplier.verify_code}
      </div>
    </Link>
  );
}
